
import React from 'react';
import { Button } from "@/components/ui/button";
import { Trophy, RefreshCw, BookOpen, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuizResultsProps {
  correctAnswers: number;
  totalQuestions: number;
  onRetry: () => void;
  onNewTopic: () => void;
}

const QuizResults: React.FC<QuizResultsProps> = ({
  correctAnswers,
  totalQuestions,
  onRetry,
  onNewTopic,
}) => {
  const percentage = Math.round((correctAnswers / totalQuestions) * 100);
  const isPassing = percentage >= 70;
  
  const getMessage = () => {
    if (percentage >= 90) return "Excellent mastery of Arabic grammar!";
    if (percentage >= 80) return "Great understanding of the concepts!";
    if (percentage >= 70) return "Good work! You're making progress.";
    if (percentage >= 60) return "You're on the right track, but need more practice.";
    return "Keep studying these concepts and try again.";
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-xl shadow-soft-lg p-6 md:p-8 animate-scale-in">
      <Button 
        variant="ghost" 
        size="sm"
        onClick={onNewTopic}
        className="mb-4 -ml-2 text-muted-foreground"
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        Back to Topics
      </Button>
      
      <div className="text-center">
        <div className={cn(
          "w-20 h-20 rounded-full mx-auto flex items-center justify-center",
          isPassing ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"
        )}>
          <Trophy className="h-10 w-10" />
        </div>
        
        <h2 className="text-2xl md:text-3xl font-serif font-bold mt-6">
          {isPassing ? "مَا شَاءَ ٱللَّٰهُ!" : "Keep Practicing!"}
        </h2>
        
        <p className="mt-2 text-muted-foreground">
          {getMessage()}
        </p>
        
        <div className="mt-8 bg-muted p-6 rounded-lg text-center">
          <div className="text-4xl font-bold">
            {correctAnswers}/{totalQuestions}
          </div>
          <div className="text-xl font-medium mt-2">
            {percentage}% Correct
          </div>
          <div className={cn(
            "mt-4 h-3 w-full bg-muted-foreground/20 rounded-full overflow-hidden",
          )}>
            <div 
              className={cn(
                "h-full transition-all duration-1000 ease-out",
                percentage >= 70 ? "bg-teal-500" : 
                percentage >= 50 ? "bg-amber-500" : "bg-destructive"
              )}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
        
        <div className="mt-8 flex flex-col md:flex-row gap-4">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={onRetry}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button 
            className="flex-1"
            onClick={onNewTopic}
          >
            <BookOpen className="mr-2 h-4 w-4" />
            Choose New Topic
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;
